<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.add_commitment.submit')"
    :hideSubmitButton="true"
    size="xl"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <div v-if="target" class="p-1">
        <SalesStrategyOverrideComponent v-model="ignored" :overage />

        <label class="form-label" :for="searchId">{{
          $t("forwards.deals.add_commitment.search")
        }}</label>
        <input
          :id="searchId"
          v-model="query"
          type="search"
          class="form-control"
          :placeholder="$t('forwards.deals.add_commitment.search_placeholder')"
        />

        <LoadingIndicatorComponent
          v-if="mandatesLoading"
          inline
          :message="$t('forwards.deals.add_commitment.searching')"
        />

        <div class="mandate-results list-group mt-2">
          <button
            v-for="mandate in mandates"
            :key="mandate.mandate_id"
            type="button"
            class="mandate-result list-group-item list-group-item-action py-2"
            :class="{
              active: mandate.mandate_id === selectedMandateId,
              'mandate-result--ineligible': !canBack(mandate),
            }"
            :aria-pressed="mandate.mandate_id === selectedMandateId"
            :disabled="!canBack(mandate)"
            @click="selectMandate(mandate.mandate_id)"
          >
            <span class="fw-semibold">{{ mandate.name }}</span>
            <span class="fs-10 opacity-75">
              ·
              {{
                $t("forwards.deals.add_commitment.listed", {
                  mwh: formatNumberWithSpaces(mandate.listed_volume_mwh),
                })
              }}
              ·
              <template v-if="mandate.minimum_price">{{
                $t("forwards.deals.commitments.floor", {
                  price: mandate.minimum_price,
                })
              }}</template>
              <template v-else>{{
                $t("forwards.deals.commitments.no_floor")
              }}</template>
            </span>
            <ul
              v-if="!canBack(mandate)"
              class="reasons list-unstyled mb-0 mt-1"
            >
              <li
                v-for="reason in reasonsOf(mandate)"
                :key="reason"
                class="text-body-tertiary fs-10"
              >
                {{ reason }}
              </li>
            </ul>
          </button>
        </div>

        <p
          v-if="!mandatesLoading && mandates.length === 0"
          class="text-body-tertiary fs-10 mt-2"
        >
          {{ $t("forwards.deals.add_commitment.no_mandates") }}
        </p>

        <template v-if="selectedMandate !== null">
          <hr />
          <h6>{{ $t("forwards.deals.add_commitment.takes_title") }}</h6>
          <table class="table table-sm">
            <thead>
              <tr>
                <th scope="col">
                  {{ $t("forwards.deals.plan.delivery") }}
                </th>
                <th scope="col">{{ $t("forwards.deals.plan.period") }}</th>
                <th scope="col" class="text-end">
                  {{ $t("forwards.deals.add_commitment.open") }}
                </th>
                <th scope="col" class="take-cell">
                  {{ $t("forwards.deals.add_commitment.take") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-nowrap">{{ deliveryLabel }}</td>
                <td class="text-nowrap">{{ target.periodLabel }}</td>
                <td class="text-end text-nowrap">
                  {{ formatNumberWithSpaces(target.volumeMwh) }}
                </td>
                <td class="take-cell">
                  <MwhInputComponent
                    v-model="take"
                    size="sm"
                    :min="0"
                    :max="maxTake"
                    :ariaLabel="
                      $t('forwards.deals.add_commitment.take_for', {
                        period: target.periodLabel,
                      })
                    "
                  />
                  <span class="text-body-tertiary fs-10">{{
                    $t("forwards.deals.add_commitment.max", {
                      mwh: formatNumberWithSpaces(maxTake),
                    })
                  }}</span>
                  <div
                    v-if="takeOverMax"
                    class="error-note fs-10 mt-1"
                    role="alert"
                  >
                    <span>{{
                      $t("forwards.deals.add_commitment.over_max", {
                        mwh: formatNumberWithSpaces(maxTake),
                      })
                    }}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <p class="advisory-note mb-0">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" />
            <span>{{ impactText }}</span>
          </p>
        </template>

        <p v-if="errorMessage" class="error-note mt-2 mb-0" role="alert">
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ errorMessage }}</span>
        </p>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ $t("forwards.deals.common.cancel") }}
      </button>
      <ActionButtonWithSpinner
        :busy="busy"
        :disabled="!canSubmit"
        :variant="submitVariant"
        @click="submit"
      >
        {{
          beyondStrategy
            ? $t("forwards.deals.add_commitment.submit_beyond_strategy")
            : $t("forwards.deals.add_commitment.submit")
        }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import SalesStrategyOverrideComponent from "@/forwards2/components/SalesStrategyOverrideComponent.vue";
import { useDeliveryWindowText } from "@/forwards2/deals/useDeliveryWindowText";
import type {
  CommitmentMandateRow,
  PeriodCapacity,
  DeliveryResponse,
} from "@/forwards2/forwardsTypes";
import {
  commitmentMandateOverage,
  useSalesStrategyText,
  useStrategyOverride,
} from "@/forwards2/salesStrategy";
import { debounce, uniq } from "lodash-es";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/** Type-ahead debounce, matched to the other search fields in the app. */
const SEARCH_DEBOUNCE_MS = 300;

/** The open slice a commitment is being pulled for — the delivery plan row the menu was opened on. */
export interface BackSliceTarget {
  sliceId: number;
  deliveryId: number;
  periodLabel: string;
  /** The row's open volume: the take's ceiling before the mandate's own capacity caps it further. */
  volumeMwh: number;
}

/**
 * Back one open slice from one mandate.
 *
 * The slice is chosen on the delivery plan row this modal was opened from, so the only decisions
 * left are which mandate to draw on and how much of the row to take — a lower take leaves the rest
 * open. Mandates that cannot back this period stay in the list with every failing criterion named,
 * because "why can I not use this mandate" is the question the search exists to answer — a mandate its
 * sales strategy holds back for the period included, with the date more is released.
 *
 * The sales strategy override (`useStrategyOverride`): ticking it lifts each mandate's cap from what
 * its strategy has released to its full availability — both come with every row, so nothing is
 * re-read — and a take beyond the strategy raises the banner and turns the button red.
 */
const props = withDefaults(
  defineProps<{
    /** Null while the modal is closed — the page has no row selected then. */
    target: BackSliceTarget | null;
    deliveries: DeliveryResponse[];
    mandates: CommitmentMandateRow[];
    mandatesLoading?: boolean;
    /** The deal's open volume before this commitment, for the impact preview. */
    openVolumeMwh: number;
    busy?: boolean;
    errorMessage?: string | null;
  }>(),
  { mandatesLoading: false, busy: false, errorMessage: null }
);

const emit = defineEmits<{
  search: [query: string];
  submit: [
    mandateId: number,
    takes: Record<number, number>,
    ignoreSalesStrategy: boolean,
  ];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();
const deliveryWindowText = useDeliveryWindowText();
const { heldBackReason } = useSalesStrategyText();

const searchId = useId();
const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};
const query = ref("");
const selectedMandateId = ref<number | null>(null);
const take = ref<number | null>(null);

const title = computed(() =>
  props.target === null
    ? ""
    : t("forwards.deals.add_commitment.title_back", {
        period: props.target.periodLabel,
      })
);

const deliveryLabel = computed(() => {
  const delivery = props.deliveries.find(
    (candidate) => candidate.id === props.target?.deliveryId
  );
  return delivery === undefined ? "" : deliveryWindowText(delivery);
});

const selectedMandate = computed(
  () =>
    props.mandates.find(
      (mandate) => mandate.mandate_id === selectedMandateId.value
    ) ?? null
);

/**
 * The take's ceiling: the row's own open volume, and the mandate's availability in the row's period
 * *and* in every wider period containing it. The server measures a take at every containing width,
 * so a take that fits its own month can still overdraw its quarter (RV2-commitments-04).
 */
const maxTake = computed(() => {
  const mandate = selectedMandate.value;
  if (mandate === null || props.target === null || !backsPeriod(mandate))
    return 0;
  return Math.min(props.target.volumeMwh, capacityFor(mandate));
});

/**
 * The whole row by default, held to the mandate's strategy even under the override, so selecting a
 * mandate never goes past its strategy by itself. A mandate whose strategy has released none of its
 * volume yet is selectable only under the override, and any take from it is beyond the strategy, so
 * it starts at the full ceiling.
 */
const defaultTake = computed(() => {
  const mandate = selectedMandate.value;
  if (mandate === null || props.target === null) return 0;
  const sellable = Math.max(0, periodCapacity(mandate)?.sellable_mwh ?? 0);
  const withinStrategy = Math.min(maxTake.value, sellable);
  return withinStrategy > 0 ? withinStrategy : maxTake.value;
});

const impactText = computed(() =>
  t("forwards.deals.add_commitment.impact", {
    from: formatNumberWithSpaces(props.openVolumeMwh),
    to: formatNumberWithSpaces(
      Math.max(0, props.openVolumeMwh - (take.value ?? 0))
    ),
  })
);

const takeOverMax = computed(() => (take.value ?? 0) > maxTake.value);

const { ignored, overage, beyondStrategy, submitVariant } = useStrategyOverride(
  model,
  () => {
    const mandate = selectedMandate.value;
    const target = props.target;
    if (mandate === null || target === null) return null;
    return commitmentMandateOverage(
      mandate,
      target.periodLabel,
      take.value ?? 0
    );
  }
);

const canSubmit = computed(
  () =>
    !props.busy &&
    selectedMandateId.value !== null &&
    (take.value ?? 0) > 0 &&
    !takeOverMax.value
);

/** Every reason the mandate does not fit this period, mandate-level and per-device, each named once. */
const reasonsOf = (mandate: CommitmentMandateRow) => {
  const failures = uniq(
    (
      mandate.fit_by_period[props.target?.periodLabel ?? ""]?.failures ??
      mandate.fit.failures
    ).map((failure) => failure.detail)
  );
  const capacity = periodCapacity(mandate);
  if (backsPeriod(mandate) && capacityFor(mandate) === 0)
    failures.push(
      capacity !== undefined && capacity.held_back_mwh > 0
        ? heldBackReason(capacity.next_release?.opens_on ?? null)
        : t("forwards.deals.add_commitment.no_availability", {
            period: props.target?.periodLabel ?? "",
          })
    );
  return failures;
};

/** The mandate's figures for the row's period; undefined when it is not one the mandate was judged on. */
function periodCapacity(
  mandate: CommitmentMandateRow
): PeriodCapacity | undefined {
  return props.target === null
    ? undefined
    : mandate.capacity_by_period[props.target.periodLabel];
}

/**
 * What the mandate can still give in the row's period: what its strategy has released, or its full
 * availability under the override. The server's figures already hold every wider period containing
 * it — a take that fits its own month can still overdraw its quarter (RV2-commitments-04). Zero
 * when the row's period is not one the mandate was judged on.
 */
function capacityFor(mandate: CommitmentMandateRow): number {
  const capacity = periodCapacity(mandate);
  if (capacity === undefined) return 0;
  return Math.max(
    0,
    ignored.value ? capacity.available_mwh : capacity.sellable_mwh
  );
}

/** Selectable: eligible for the row's period and with something left to give there. */
function canBack(mandate: CommitmentMandateRow): boolean {
  return backsPeriod(mandate) && capacityFor(mandate) > 0;
}

/**
 * Whether the mandate may back this period at all. A take is judged per period on the backend, and
 * OK-power "new" expires with the device's age, so a mandate that fails one period can still back
 * another (RV2-commitments-02). An older payload without the per-period map falls back to the
 * mandate-wide verdict.
 */
function backsPeriod(mandate: CommitmentMandateRow): boolean {
  const label = props.target?.periodLabel ?? "";
  return mandate.fit_by_period[label]?.eligible ?? mandate.fit.eligible;
}

const selectMandate = (mandateId: number) => {
  selectedMandateId.value = mandateId;
  take.value = defaultTake.value === 0 ? null : defaultTake.value;
};

const runSearch = debounce((value: string) => {
  emit("search", value);
}, SEARCH_DEBOUNCE_MS);

watch(query, (value) => runSearch(value));

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (!isOpen) return;
  query.value = "";
  selectedMandateId.value = null;
  take.value = null;
  emit("search", "");
});

const close = () => {
  model.value = false;
};

const submit = () => {
  if (selectedMandateId.value === null || props.target === null) return;
  const taken = take.value ?? 0;
  if (taken <= 0) return;
  emit(
    "submit",
    selectedMandateId.value,
    { [props.target.sliceId]: taken },
    ignored.value
  );
};
</script>

<style scoped lang="scss">
.mandate-results {
  max-height: 16rem;
  overflow-y: auto;
}

.mandate-result--ineligible {
  opacity: 0.75;
}

.take-cell {
  min-width: 10rem;
}
</style>
